import { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react"
import { SCHEDULES_API_PATH, SCHEDULES_LIST_LIMIT } from "../constants"
import { StaffSchedule } from "../interfaces"
import { dayKeyFromApi } from "../model/mskCalendar"
import { ScheduleListArgs, splitRangeIntoWeeks } from "../model/period"
import { cellKey } from "../model/scheduleCells"

/**
 * В queryFn RTK передаёт не исходный трёхаргументный BaseQueryFn, а уже связанный
 * одноаргументный wrapper. Типизируем именно его — BaseQueryFn потребовал бы api и
 * extraOptions, хотя в runtime их уже подставил RTK.
 */
type BaseQueryResult =
  | { data: unknown; error?: undefined }
  // baseQuery объявлен в api как BaseQueryFn без параметра Error, поэтому queryFn-wrapper
  // вправе вернуть unknown. К FetchBaseQueryError приводим только на границе Result ниже.
  | { error: unknown; data?: undefined }
type BaseQuery = (
  arg: string | FetchArgs,
) => BaseQueryResult | PromiseLike<BaseQueryResult>
type Result =
  | { data: StaffSchedule.ScheduleMap }
  | { error: FetchBaseQueryError }

const customError = (error: string): Result => ({
  error: { status: "CUSTOM_ERROR", error },
})

const toScheduleMap = (
  list: StaffSchedule.Schedule[],
): StaffSchedule.ScheduleMap => {
  const map: StaffSchedule.ScheduleMap = {}
  for (const s of list) {
    map[cellKey(s.staff_id, dayKeyFromApi(s.date))] = s
  }
  return map
}

const fetchList = (baseQuery: BaseQuery, dateFrom: string, dateTo: string) =>
  baseQuery({
    url: `${SCHEDULES_API_PATH}list`,
    method: "GET",
    params: {
      date_from: dateFrom,
      date_to: dateTo,
      limit: SCHEDULES_LIST_LIMIT,
    },
  })

/**
 * GET /schedules/list → ScheduleMap. Единственное место, знающее транспорт чтения списка.
 *
 * `page` не используем: сортировка ответа контрактом не гарантирована, постраничный обход
 * неупорядоченной выборки может продублировать и потерять записи. Вместо этого:
 * 1. один запрос на весь период с limit=1000 (максимум по контракту);
 * 2. ответ ровно на limit записей МОГ быть срезан сервером — тогда период разбивается на
 *    недельные подзапросы (параллельно), результаты склеиваются с дедупликацией по ключу.
 *    Ложное срабатывание на ровно 1000 записях безвредно (цена — 2–6 лишних запросов);
 * 3. срез недельного подзапроса — ошибка с внятным текстом: неполные данные в этом экране
 *    визуально неотличимы от потери смен, показывать их нельзя.
 */
export const fetchScheduleMap = async (
  baseQuery: BaseQuery,
  args: ScheduleListArgs,
): Promise<Result> => {
  try {
    const first = await fetchList(baseQuery, args.date_from, args.date_to)
    if (first.error) return { error: first.error as FetchBaseQueryError }
    const list = first.data as StaffSchedule.Schedule[]
    if (!Array.isArray(list)) {
      return customError("schedules/list: неожиданный формат ответа")
    }
    if (list.length < SCHEDULES_LIST_LIMIT) {
      return { data: toScheduleMap(list) }
    }

    const weeks = splitRangeIntoWeeks(args.date_from, args.date_to)
    const parts = await Promise.all(
      weeks.map((w) => fetchList(baseQuery, w.from, w.to)),
    )
    const merged: StaffSchedule.ScheduleMap = {}
    for (const part of parts) {
      if (part.error) return { error: part.error as FetchBaseQueryError }
      const partList = part.data as StaffSchedule.Schedule[]
      if (!Array.isArray(partList)) {
        return customError("schedules/list: неожиданный формат ответа")
      }
      if (partList.length >= SCHEDULES_LIST_LIMIT) {
        return customError(
          `Слишком много записей расписания за период ${args.date_from} — ${args.date_to}. Сократите период.`,
        )
      }
      Object.assign(merged, toScheduleMap(partList))
    }
    return { data: merged }
  } catch (e) {
    // сюда попадает и throw из dayKeyFromApi на битой дате
    return customError(
      e instanceof Error
        ? e.message
        : "schedules/list: ошибка обработки ответа",
    )
  }
}
